import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Leaf } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const isActive = (path: string) => location.pathname === path;

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    setIsMenuOpen(false);

    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-gray-900 border-b border-gray-800 sticky top-0 z-50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex-shrink-0">
                <img
                  src="/WhatsApp_Image_2025-11-12_at_14.04.29.jpeg"
                  alt="Heitje"
                  className="h-10 w-auto"
                />
              </Link>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <a
                  href="#features"
                  onClick={(e) => handleAnchorClick(e, 'features')}
                  className="px-3 py-2 text-sm font-medium transition-colors text-white/90 hover:text-white"
                >
                  {t.nav.features}
                </a>
                <a
                  href="#how-it-works"
                  onClick={(e) => handleAnchorClick(e, 'how-it-works')}
                  className="px-3 py-2 text-sm font-medium transition-colors text-white/90 hover:text-white"
                >
                  {t.nav.howItWorks}
                </a>
                <a
                  href="#impact"
                  onClick={(e) => handleAnchorClick(e, 'impact')}
                  className="px-3 py-2 text-sm font-medium transition-colors text-white/90 hover:text-white"
                >
                  {t.nav.impact}
                </a>
                <LanguageSwitcher />
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={toggleMenu}
                className="inline-flex items-center justify-center p-2 rounded-md text-white hover:text-white hover:bg-gray-800 transition-colors"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-gray-900 border-t border-gray-800">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              <a
                href="#features"
                onClick={(e) => handleAnchorClick(e, 'features')}
                className="block px-3 py-2 text-white/90 hover:text-white transition-colors"
              >
                {t.nav.features}
              </a>
              <a
                href="#how-it-works"
                onClick={(e) => handleAnchorClick(e, 'how-it-works')}
                className="block px-3 py-2 text-white/90 hover:text-white transition-colors"
              >
                {t.nav.howItWorks}
              </a>
              <a
                href="#impact"
                onClick={(e) => handleAnchorClick(e, 'impact')}
                className="block px-3 py-2 text-white/90 hover:text-white transition-colors"
              >
                {t.nav.impact}
              </a>
              <div className="px-3 py-2">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <img
                src="/WhatsApp_Image_2025-11-12_at_14.04.29.jpeg"
                alt="Heitje"
                className="h-16 w-auto mb-4"
              />
              <p className="text-gray-400 mb-6 max-w-md">
                {t.footer.tagline}
              </p>
              <div className="flex items-center space-x-4">
                <Leaf className="h-6 w-6 text-lime-400" />
                <span className="text-gray-400">{t.footer.certified}</span>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">{t.footer.features}</h4>
              <ul className="space-y-2">
                <li><a href="https://www.statiegeldnederland.nl/locatiewijzer" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">{t.footer.findCollectionPoints}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">{t.footer.schedulePickups}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">{t.footer.trackEarnings}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">{t.footer.impactDashboard}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">{t.footer.support}</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">{t.footer.helpCenter}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">{t.footer.contact}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">{t.footer.becomeCollector}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">{t.footer.becomePartner}</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 mb-4 md:mb-0">
              {t.footer.copyright}
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                {t.footer.privacy}
              </Link>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                {t.footer.terms}
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                {t.footer.contact}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}